import { ref, onBeforeUnmount } from 'vue';

// SiteUp — holofote do Modo Foco (fatia 12/13): retângulo ancorado no DOM
// real + véu. Um mecanismo só, reusado no painel de config e no guia.
export const SPOTLIGHT_PAD_PX = 8;

export const SPOTLIGHT_HOLE_CLASS =
  'pointer-events-none fixed z-[70] rounded-[14px] shadow-[0_0_0_9999px_rgb(var(--slate-12)/0.42),0_0_0_2px_rgb(var(--iris-9))] transition-[top,left,width,height] duration-[length:var(--motion-duration-layout)] ease-[var(--motion-ease-in-out)] dark:shadow-[0_0_0_9999px_rgb(0_0_0_/_0.62),0_0_0_2px_rgb(var(--iris-9))]';

export function rectFromElement(el, pad = SPOTLIGHT_PAD_PX) {
  if (!el || typeof el.getBoundingClientRect !== 'function') return null;
  const box = el.getBoundingClientRect();
  if (box.width < 2 || box.height < 2) return null;
  return {
    top: box.top - pad,
    left: box.left - pad,
    width: box.width + pad * 2,
    height: box.height + pad * 2,
    right: box.right + pad,
    bottom: box.bottom + pad,
  };
}

export function findSpotTarget(selector) {
  if (!selector || typeof document === 'undefined') return null;
  const el = document.querySelector(selector);
  return rectFromElement(el) ? el : null;
}

export function useFocusSpotlight() {
  const spotlightStyle = ref(null);
  const spotlightRect = ref(null);

  let selector = '';
  let resizeObserver = null;
  let mutationObserver = null;
  let measureRaf = 0;
  let extraSelectors = [];

  const applyRect = box => {
    spotlightRect.value = box;
    if (!box) {
      spotlightStyle.value = null;
      return;
    }
    spotlightStyle.value = {
      top: `${box.top}px`,
      left: `${box.left}px`,
      width: `${box.width}px`,
      height: `${box.height}px`,
    };
  };

  const measure = () => {
    const el = findSpotTarget(selector);
    applyRect(rectFromElement(el));
    return el;
  };

  const scheduleMeasure = () => {
    if (typeof window === 'undefined') return;
    if (measureRaf) return;
    measureRaf = window.requestAnimationFrame(() => {
      measureRaf = 0;
      measure();
    });
  };

  const detach = () => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', scheduleMeasure);
      window.removeEventListener('scroll', scheduleMeasure, true);
      if (measureRaf) {
        window.cancelAnimationFrame(measureRaf);
        measureRaf = 0;
      }
    }
    resizeObserver?.disconnect();
    resizeObserver = null;
    mutationObserver?.disconnect();
    mutationObserver = null;
  };

  const attach = (anchors = []) => {
    detach();
    extraSelectors = anchors.filter(Boolean);
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    window.addEventListener('resize', scheduleMeasure);
    window.addEventListener('scroll', scheduleMeasure, true);
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleMeasure);
      resizeObserver.observe(document.documentElement);
      extraSelectors.forEach(sel => {
        const el = document.querySelector(sel);
        if (el) resizeObserver.observe(el);
      });
    }
    if (typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(scheduleMeasure);
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
    measure();
  };

  const setSelector = next => {
    selector = next || '';
    measure();
  };

  onBeforeUnmount(detach);

  return {
    spotlightStyle,
    spotlightRect,
    setSelector,
    measure,
    attach,
    detach,
    scheduleMeasure,
  };
}
